import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, TrendingUp, Building2, DollarSign, Ruler, ArrowRight } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { api } from '../../services/api';
import {
  ACCESS_STATS_DASHBOARD,
  ACCESS_STATS_OCCUPANCY,
  ACCESS_STATS_PRICE_M2,
  ACCESS_STATS_REVENUE,
  ACCESS_STATS_UNITS,
  usePermissions,
} from '../../hooks/usePermissions';

export const StatsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [revenueData, setRevenueData] = useState<{ month: string; revenue: number }[]>([]);
  const [occupancyData, setOccupancyData] = useState<{ property: string; occupied: number; vacant: number }[]>([]);
  const [rentPerM2Summary, setRentPerM2Summary] = useState<{
    averageRentPerM2: number;
    maxRentPerM2: number;
    minRentPerM2: number;
  } | null>(null);

  const { hasPermission, hasAnyPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');

  const canDash = hasAnyPermission([...ACCESS_STATS_DASHBOARD]);
  const canOcc = hasAnyPermission([...ACCESS_STATS_OCCUPANCY]);
  const canRev = hasAnyPermission([...ACCESS_STATS_REVENUE]);
  const canPrice = hasAnyPermission([...ACCESS_STATS_PRICE_M2]);
  const canUnits = hasAnyPermission([...ACCESS_STATS_UNITS]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (canDash) {
        try {
          setMetrics(await api.getDashboardMetrics());
        } catch (e: any) {
          setMetrics(null);
          setError(e?.message || 'Error al cargar métricas');
        }
      } else {
        setMetrics(null);
      }
      if (canRev) {
        try {
          const r = await api.getRevenueData();
          setRevenueData(r || []);
        } catch {
          setRevenueData([]);
        }
      } else {
        setRevenueData([]);
      }
      if (canOcc) {
        try {
          const o = await api.getOccupancyData();
          setOccupancyData(o || []);
        } catch {
          setOccupancyData([]);
        }
      } else {
        setOccupancyData([]);
      }
      if (canPrice) {
        try {
          const rentPerM2Res = await api.getRentPerM2Stats();
          setRentPerM2Summary(rentPerM2Res?.summary ?? null);
        } catch {
          setRentPerM2Summary(null);
        }
      } else {
        setRentPerM2Summary(null);
      }
    } finally {
      setLoading(false);
    }
  }, [canDash, canOcc, canRev, canPrice]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalUnits = occupancyData.reduce((sum, p) => sum + p.occupied + p.vacant, 0);
  const totalOccupied = occupancyData.reduce((sum, p) => sum + p.occupied, 0);
  const totalVacant = totalUnits - totalOccupied;
  const averageOccupancy = totalUnits > 0 ? (totalOccupied / totalUnits) * 100 : metrics?.averageOccupancy ?? 0;
  const totalRevenue = revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0);
  const averageRevenue = revenueData.length > 0 ? totalRevenue / revenueData.length : 0;
  const averageRentPerM2 = rentPerM2Summary?.averageRentPerM2 ?? 0;

  const statsCards = useMemo(
    () =>
      (
        [
          {
            show: canOcc,
            title: 'Ocupación',
            icon: TrendingUp,
            value: `${Number(averageOccupancy).toFixed(1)}%`,
            subtitle: `${totalOccupied} de ${totalUnits} unidades ocupadas`,
            color: 'text-primary',
            bgColor: 'bg-primary/10',
            path: '/estadisticas/ocupacion',
          },
          {
            show: canUnits,
            title: 'Unidades',
            icon: Building2,
            value: totalUnits.toString(),
            subtitle: `${totalOccupied} ocupadas, ${totalVacant} vacantes`,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            path: '/estadisticas/unidades',
          },
          {
            show: canRev,
            title: 'Ingresos Brutos',
            icon: DollarSign,
            value: canViewPrices ? `$${Math.round(averageRevenue).toLocaleString()}` : '—',
            subtitle: canViewPrices
              ? `Promedio mensual (Total: $${Math.round(totalRevenue).toLocaleString()})`
              : 'Sin permiso para ver importes',
            color: 'text-green-600',
            bgColor: 'bg-green-50',
            path: '/estadisticas/ingresos-brutos',
          },
          {
            show: canPrice,
            title: 'Valor m²',
            icon: Ruler,
            value: canViewPrices ? `$${Math.round(averageRentPerM2).toLocaleString()}` : '—',
            subtitle: 'Renta promedio por m²',
            color: 'text-purple-600',
            bgColor: 'bg-purple-50',
            path: '/estadisticas/valor-m2',
          },
        ] as const
      )
        .filter((c) => c.show)
        .map(({ show: _s, ...rest }) => rest),
    [
      canOcc,
      canUnits,
      canRev,
      canPrice,
      canViewPrices,
      averageOccupancy,
      totalOccupied,
      totalUnits,
      totalVacant,
      averageRevenue,
      totalRevenue,
      averageRentPerM2,
    ],
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <LayoutDashboard className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Dashboard de Estadísticas</h1>
        </div>
        <div className="flex justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <LayoutDashboard className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Dashboard de Estadísticas</h1>
        </div>
        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 text-sm font-medium"
        >
          Actualizar datos
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">
          {error}
        </div>
      )}

      {statsCards.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">
          Tu rol no incluye permisos de resumen para estas tarjetas. Usa el menú lateral para abrir solo las secciones
          que te asignaron.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.title}
                className="cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => navigate(stat.path)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
                <h3 className="text-sm font-medium text-gray-600 mb-2">{stat.title}</h3>
                <p className={`text-3xl font-bold ${stat.color} mb-1`}>{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.subtitle}</p>
              </Card>
            );
          })}
        </div>
      )}

      {(canOcc || canUnits || canRev || canPrice) && (
        <Card title="Acceso Rápido a Estadísticas Detalladas">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {canOcc && (
              <button
                type="button"
                onClick={() => navigate('/estadisticas/ocupacion')}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span className="font-medium text-gray-900">Ocupación</span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </button>
            )}
            {canUnits && (
              <button
                type="button"
                onClick={() => navigate('/estadisticas/unidades')}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <Building2 className="w-5 h-5 text-primary" />
                  <span className="font-medium text-gray-900">Unidades</span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </button>
            )}
            {canRev && (
              <button
                type="button"
                onClick={() => navigate('/estadisticas/ingresos-brutos')}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <span className="font-medium text-gray-900">Ingresos Brutos</span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </button>
            )}
            {canPrice && (
              <button
                type="button"
                onClick={() => navigate('/estadisticas/valor-m2')}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-left"
              >
                <div className="flex items-center space-x-3">
                  <Ruler className="w-5 h-5 text-primary" />
                  <span className="font-medium text-gray-900">Valor m²</span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};
